import React, { useCallback, useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Chip,
  Avatar,
  CircularProgress,
} from "@mui/material";
import { Person } from "@mui/icons-material";
import { AssignMemoryBookModal } from "./AssignMemoryBookModal";
import apiService from "../../../service/Api";

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  profile_picture_url: string | null;
}

interface MemoryBookAssignment {
  id: number;
  assigned_to: User;
  physical_comments: number | null;
}

interface MemoryBookCardProps {
  streamId?: number;
  deceasedName?: string;
}

const MemoryBookCard: React.FC<MemoryBookCardProps> = ({
  streamId,
  deceasedName = "",
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [assignees, setAssignees] = useState<User[]>([]);
  const [collectedComments, setCollectedComments] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const fetchAssignment = useCallback(async () => {
    if (!streamId) return;

    setLoading(true);
    try {
      const response = await apiService.get<{
        results: MemoryBookAssignment[];
      }>(`/api/streams/memory-book-assignment/?stream=${streamId}`);

      const results = response.data.results || [];
      setAssignees(results.map((a) => a.assigned_to));
      setCollectedComments(String(results[0]?.physical_comments ?? ""));
    } catch (error) {
      console.error("Failed to fetch assignment:", error);
    } finally {
      setLoading(false);
    }
  }, [streamId]);

  useEffect(() => {
    fetchAssignment();
  }, [fetchAssignment]);

  if (loading) {
    return (
      <Grid>
        <Card sx={{ boxShadow: 0, height: "auto", mt: 2 }}>
          <CardContent>
            <Box textAlign="center" py={2}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" mt={1}>
                Loading assignment...
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  }

  return (
    <>
      <Grid>
        <Card sx={{ boxShadow: 0, height: "auto", mt: 2 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Assign Memory Book
              </Typography>
              <Button
                variant="contained"
                onClick={() => setModalOpen(true)}
                sx={{ textTransform: "none" }}
              >
                {assignees.length > 0 ? "Reassign" : "Assign Memory Book"}
              </Button>
            </Box>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                {assignees.length > 0 ? (
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Assigned to:
                      </Typography>
                      {assignees.map((user) => (
                        <Chip
                          key={user.id}
                          avatar={
                            user.profile_picture_url ? (
                              <Avatar src={user.profile_picture_url} />
                            ) : (
                              <Avatar>
                                <Person fontSize="small" />
                              </Avatar>
                            )
                          }
                          label={`${user.first_name} ${user.last_name}`}
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Box>
                    {collectedComments && (
                      <Typography variant="body2" color="text.secondary">
                        Collected Comments: {collectedComments}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No memory book assigned yet.
                  </Typography>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <AssignMemoryBookModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        deceasedName={deceasedName}
        streamId={streamId ?? 0}
        onAssignSuccess={fetchAssignment}
      />
    </>
  );
};

export default MemoryBookCard;